/**
 * Hero CTA placement telemetry — decide WHERE to put the CTA in
 * BaseTemplateBlack (public AR home via Index alias) given /admin/cta-por-region.
 *
 * Slot IDs are stable product labels (not CSS classes). Current AR home:
 *   hero_below_title — under H1/subtitle in the hero section.
 */

export const HERO_CTA_PLACEMENT_SLOT = {
  /** BaseTemplateBlack (AR public): CTA under hero title block. */
  HERO_BELOW_TITLE: "hero_below_title",
} as const;

export type HeroCtaPlacementSlot =
  (typeof HERO_CTA_PLACEMENT_SLOT)[keyof typeof HERO_CTA_PLACEMENT_SLOT];

export type HeroCtaPlacementSnapshot = {
  hero_cta_placement_slot: HeroCtaPlacementSlot | string;
  hero_cta_layout_section: "hero";
  hero_cta_template: "BaseTemplate" | "BaseTemplateBlack" | string;
  viewport_w: number;
  viewport_h: number;
  viewport_aspect: number;
  device_class: "mobile" | "tablet" | "desktop";
  scroll_y: number;
  cta_top_px: number;
  cta_bottom_px: number;
  cta_height_px: number;
  cta_visible_ratio: number;
  /** Any part of CTA intersects the viewport. */
  cta_intersects_viewport: boolean;
  /** Fully inside first fold (no scroll needed to see entire CTA). */
  cta_fully_in_first_fold: boolean;
  /**
   * Alias of cta_fully_in_first_fold — use this in HogQL dashboards.
   * (Previously meant "any intersection + little scroll"; fixed for prod decisions.)
   */
  cta_in_first_viewport: boolean;
  cta_fully_visible: boolean;
  document_h: number;
  cta_doc_y_pct: number;
};

function deviceClass(w: number): "mobile" | "tablet" | "desktop" {
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Measure DOM placement for PostHog extras (AR hero CTA). */
export function measureHeroCtaPlacement(
  el: Element | null | undefined,
  opts: {
    slot: HeroCtaPlacementSlot | string;
    template: string;
  },
): HeroCtaPlacementSnapshot | null {
  if (typeof window === "undefined" || !el) return null;
  const rect = el.getBoundingClientRect();
  const vw = Math.max(1, window.innerWidth || 0);
  const vh = Math.max(1, window.innerHeight || 0);
  const scrollY = Math.round(window.scrollY || window.pageYOffset || 0);
  const docH = Math.max(
    1,
    document.documentElement?.scrollHeight || document.body?.scrollHeight || vh,
  );
  const visible =
    Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0)) /
    Math.max(1, rect.height || 1);
  const ratio = Math.max(0, Math.min(1, Number.isFinite(visible) ? visible : 0));
  const topDoc = scrollY + rect.top;
  const intersects = rect.bottom > 0 && rect.top < vh && rect.height > 0;
  const fullyInFirstFold =
    scrollY <= 2 && rect.top >= 0 && rect.bottom <= vh && rect.height > 0;

  return {
    hero_cta_placement_slot: opts.slot,
    hero_cta_layout_section: "hero",
    hero_cta_template: opts.template,
    viewport_w: Math.round(vw),
    viewport_h: Math.round(vh),
    viewport_aspect: round1(vw / vh),
    device_class: deviceClass(vw),
    scroll_y: scrollY,
    cta_top_px: Math.round(rect.top),
    cta_bottom_px: Math.round(rect.bottom),
    cta_height_px: Math.round(rect.height),
    cta_visible_ratio: round1(ratio),
    cta_intersects_viewport: intersects,
    cta_fully_in_first_fold: fullyInFirstFold,
    cta_in_first_viewport: fullyInFirstFold,
    cta_fully_visible: ratio >= 0.98,
    document_h: Math.round(docH),
    cta_doc_y_pct: round1((topDoc / docH) * 100),
  };
}

export type HeroCtaVisibilityState = {
  firstVisibleAtMs: number | null;
  maxVisibleRatio: number;
  visibleMs: number;
  lastPlacement: HeroCtaPlacementSnapshot | null;
  pageLoadMs: number;
};

/**
 * Observe hero CTA visibility. Calls onVisible once when ratio >= threshold.
 * Accumulates visible time for click enrichment (seconds_cta_visible).
 */
export function observeHeroCtaPlacement(
  el: Element,
  opts: {
    slot: HeroCtaPlacementSlot | string;
    template: string;
    threshold?: number;
    onVisible?: (snap: HeroCtaPlacementSnapshot, state: HeroCtaVisibilityState) => void;
  },
): { disconnect: () => void; getState: () => HeroCtaVisibilityState } {
  const pageLoadMs =
    typeof performance !== "undefined" && performance.timeOrigin
      ? performance.timeOrigin
      : Date.now();
  const state: HeroCtaVisibilityState = {
    firstVisibleAtMs: null,
    maxVisibleRatio: 0,
    visibleMs: 0,
    lastPlacement: null,
    pageLoadMs,
  };
  let visibleSince: number | null = null;
  let firedVisible = false;
  const threshold = opts.threshold ?? 0.35;

  const tickVisibleTime = (now: number, ratio: number) => {
    if (ratio >= threshold) {
      if (visibleSince == null) visibleSince = now;
      if (state.firstVisibleAtMs == null) state.firstVisibleAtMs = now;
    } else if (visibleSince != null) {
      state.visibleMs += now - visibleSince;
      visibleSince = null;
    }
  };

  const applySnap = (snap: HeroCtaPlacementSnapshot, now: number, ratio: number) => {
    state.lastPlacement = snap;
    state.maxVisibleRatio = Math.max(state.maxVisibleRatio, ratio);
    tickVisibleTime(now, ratio);
    if (!firedVisible && ratio >= threshold) {
      firedVisible = true;
      opts.onVisible?.(snap, { ...state });
    }
  };

  const io =
    typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            const now = Date.now();
            const ratio = entry.intersectionRatio ?? 0;
            const snap = measureHeroCtaPlacement(el, {
              slot: opts.slot,
              template: opts.template,
            });
            if (snap) applySnap(snap, now, Math.max(ratio, snap.cta_visible_ratio));
          },
          {
            threshold: [0, 0.25, 0.35, 0.5, 0.75, 1],
            root: null,
            rootMargin: "0px",
          },
        )
      : null;

  io?.observe(el);

  const immediate = measureHeroCtaPlacement(el, {
    slot: opts.slot,
    template: opts.template,
  });
  if (immediate) {
    applySnap(immediate, Date.now(), immediate.cta_visible_ratio);
  }

  return {
    disconnect: () => {
      if (visibleSince != null) {
        state.visibleMs += Date.now() - visibleSince;
        visibleSince = null;
      }
      io?.disconnect();
    },
    getState: () => {
      const live =
        measureHeroCtaPlacement(el, {
          slot: opts.slot,
          template: opts.template,
        }) || state.lastPlacement;
      const visibleMs =
        visibleSince != null
          ? state.visibleMs + (Date.now() - visibleSince)
          : state.visibleMs;
      return {
        ...state,
        visibleMs,
        lastPlacement: live,
        maxVisibleRatio: Math.max(
          state.maxVisibleRatio,
          live?.cta_visible_ratio ?? 0,
        ),
      };
    },
  };
}

/**
 * Flatten visibility + placement for extras.
 * `forClick: true` → seconds_to_click / seconds_from_load (only on real clicks).
 * Impressions / page_enter omit those so HogQL stays clean.
 */
export function heroCtaPlacementAnalyticsProps(
  state: HeroCtaVisibilityState | null | undefined,
  opts?: { forClick?: boolean; clickAtMs?: number },
): Record<string, string | number | boolean | undefined> {
  if (!state?.lastPlacement) return {};
  const p = state.lastPlacement;
  const out: Record<string, string | number | boolean | undefined> = {
    ...p,
    seconds_cta_visible: round1(state.visibleMs / 1000),
    cta_max_visible_ratio: round1(state.maxVisibleRatio),
  };
  if (state.firstVisibleAtMs != null) {
    out.seconds_to_cta_visible = round1(
      Math.max(0, (state.firstVisibleAtMs - state.pageLoadMs) / 1000),
    );
  }
  if (opts?.forClick) {
    const clickAt = opts.clickAtMs ?? Date.now();
    if (state.firstVisibleAtMs != null) {
      out.seconds_to_click = round1(
        Math.max(0, (clickAt - state.firstVisibleAtMs) / 1000),
      );
    }
    out.seconds_from_load = round1(
      Math.max(0, (clickAt - state.pageLoadMs) / 1000),
    );
  }
  return out;
}

/** Sync measure when observer is not ready yet (impression / page_enter). */
export function measureHeroCtaPlacementProps(
  el: Element | null | undefined,
  opts: { slot: HeroCtaPlacementSlot | string; template: string },
): Record<string, string | number | boolean | undefined> {
  const snap = measureHeroCtaPlacement(el, opts);
  if (!snap) return {};
  return { ...snap };
}
